import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, filter, map, Observable, timer } from 'rxjs';
import { retry } from 'rxjs/operators';
import { WebSocketSubject, webSocket } from 'rxjs/webSocket';
import { MessageService } from 'primeng/api';

import { RECONNECT_INTERVAL } from '@service/websocket.service';
import { environment } from '../environment/environment';
import { WsAuthLoginPayload, WsAuthSuccessPayload, WsErrorPayload, WsMessage, WsMessageType } from '@type/ws.type';

const WS_RETRY_COUNT = 5;
const WS_RETRY_DELAY = 5000;

@Injectable({
  providedIn: 'root',
})
export class WebsocketSimpleService {
  private incomingMessages$ = new BehaviorSubject<any>(null);
  private authState$: Observable<boolean> = this.getFilteredMessages<WsMessage<WsAuthSuccessPayload>>((message: WsMessage<WsAuthSuccessPayload>) => message?.type === WsMessageType.authSuccess)
    .pipe(
      map(() => true),
    );
  private connectionStatus$ = new BehaviorSubject<boolean>(false);

  private webSocket$: WebSocketSubject<any>;
  private readonly messageService = inject(MessageService);

  public connect(token: string): void {
    this.webSocket$ = webSocket({
      url: environment.websocketUrl,
      openObserver: {
        next: () => {
          console.log('Websocket connected');
          this.connectionStatus$.next(true);
          this.initMessage(token);
        },
      },
    });

    this.webSocket$.pipe(
      retry({
        count: WS_RETRY_COUNT,
        delay: () => {
          console.log(`Websocket connection down, will attempt reconnection in ${RECONNECT_INTERVAL / 1000}s`);
          this.connectionStatus$.next(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Websocket connection down',
            detail: `Websocket connection down, will attempt reconnection in ${RECONNECT_INTERVAL / 1000}s`,
          });
          return timer(WS_RETRY_DELAY);
        },
      }),
    ).subscribe({
      next: message => {
        if (message.type === WsMessageType.error) {
          this.showWsErrorMessage(message.payload);
        } else {
          this.incomingMessages$.next(message);
        }
      },
      error: error => {
        this.connectionStatus$.next(false);
        console.error('WebSocket error:', error);
      },
      complete: () => {
        console.log('WebSocket connection closed');
      },
    });
  }

  public reconnect(token: string): void {
    this.initMessage(token);
  }

  getAuthState$(): Observable<boolean> {
    return this.authState$;
  }

  getConnectionStatus$(): Observable<boolean> {
    return this.connectionStatus$.asObservable();
  }

  sendMessage<T>(message: T): void {
    this.webSocket$.next(message);
  }

  getMessages<T>(): Observable<T> {
    return this.incomingMessages$ as Observable<T>;
  }

  getFilteredMessages<T>(filterFn: (message: T) => boolean): Observable<T> {
    return this.incomingMessages$.pipe(
      filter(filterFn),
    );
  }

  closeConnection(): void {
    this.webSocket$.complete();
  }

  private initMessage(token: string): void {
    this.sendMessage<WsMessage<WsAuthLoginPayload>>({
      type: WsMessageType.authLogin,
      payload: { token },
    });
  }

  private showWsErrorMessage(payload: WsErrorPayload): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Websocket error',
      detail: payload.message,
    });
  }
}
